<template>
  <b-modal
    id="logementStoreModal"
    ref="logementStoreModal"
    scrollable
    @show="onModalShow"
  >
    <template #modal-header>
      <h5>{{ isEdit ? $t("EDIT") : $t("NEW") }} {{ $t("HOUSING") }}</h5>
      <b-button size="sm" @click="resetModal">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="17.028"
          height="17.028"
          viewBox="0 0 17.028 17.028"
        >
          <path
            id="Icon_material-close"
            data-name="Icon material-close"
            d="M24.528,9.215,22.813,7.5l-6.8,6.8-6.8-6.8L7.5,9.215l6.8,6.8-6.8,6.8,1.715,1.715,6.8-6.8,6.8,6.8,1.715-1.715-6.8-6.8Z"
            transform="translate(-7.5 -7.5)"
            fill="#393939"
          />
        </svg>
      </b-button>
    </template>

    <form @submit.prevent="saveLogement">
      <div class="center">
        <!-- Entreprise (masqué si clientContext fourni) -->
        <b-form-group v-if="!clientContext" :label="$t('COMPANY')" label-for="entreprise">
          <multiselect
            id="entreprise"
            v-model="selectedEntreprise"
            :searchable="true"
            :close-on-select="true"
            :multiple="false"
            :options="computedEntreprises"
            label="lib"
            track-by="id"
            :placeholder="$t('Select')"
            class="form-input custom-placeholder custom-input"
            :class="{ 'is-invalid': validateState('entreprise') === false }"
          />
          <error-handle
            :list="v$.form.entreprise && v$.form.entreprise.$errors ? v$.form.entreprise.$errors : []"
            id="entreprise-feedback"
          />
        </b-form-group>

        <!-- Client (masqué si clientContext fourni) -->
        <b-form-group v-if="!clientContext" :label="$t('CUSTOMER')" label-for="client">
          <multiselect
            id="client"
            v-model="selectedClient"
            :searchable="true"
            :close-on-select="true"
            :multiple="false"
            :options="computedClients"
            label="fullName"
            track-by="id"
            :placeholder="$t('Select')"
            class="form-input custom-placeholder custom-input"
            :class="{ 'is-invalid': validateState('client_id') === false }"
          />
          <error-handle
            :list="v$.form.client_id && v$.form.client_id.$errors ? v$.form.client_id.$errors : []"
            id="client-feedback"
          />
        </b-form-group>

        <b-form-group :label="$t('Rue') + '*'" label-for="adresse">
          <b-form-input
            id="adresse"
            v-model="form.adresse"
            :state="validateState('adresse')"
            aria-describedby="adresse-feedback"
            required
          />
          <error-handle
            :list="v$.form.adresse.$errors"
            id="adresse-feedback"
          />
        </b-form-group>

        <b-form-group :label="$t('CITY') + '*'" label-for="ville" class="mt-2">
          <b-form-input
            id="ville"
            v-model="form.ville"
            :state="validateState('ville')"
            aria-describedby="ville-feedback"
            required
          />
          <error-handle
            :list="v$.form.ville.$errors"
            id="ville-feedback"
          />
        </b-form-group>

        <b-form-group :label="$t('POSTAL_CODE') + '*'" label-for="code_postal" class="mt-2">
          <b-form-input
            id="code_postal"
            v-model="form.code_postal"
            :state="validateState('code_postal')"
            aria-describedby="cp-feedback"
            required
          />
          <error-handle
            :list="v$.form.code_postal.$errors"
            id="cp-feedback"
          />
        </b-form-group>

            <b-form-group :label="$t('TYPE_LOGEMENT')" label-for="type">
              <b-form-select
                id="type"
                v-model="form.type"
                :options="optionstypelogement"
                text-field="text"
                value-field="value"
                :state="validateState('type')"
                aria-describedby="type-feedback"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    -- {{ $t("TYPE_LOGEMENT") }} --
                  </b-form-select-option>
                </template>
              </b-form-select>
              <error-handle
                :list="v$.form.type && v$.form.type.$errors ? v$.form.type.$errors : []"
                id="type-feedback"
              />
            </b-form-group>
         
            <b-form-group :label="$t('Surface habitable')" label-for="surface">
              <b-form-input
                id="surface"
                type="number"
                min="0"
                v-model="form.surface"
                :state="validateState('surface')"
                aria-describedby="surface-feedback"
              />
              <error-handle
                :list="v$.form.surface && v$.form.surface.$errors ? v$.form.surface.$errors : []"
                id="surface-feedback"
              />
            </b-form-group>
        

        <b-form-group :label="$t('TYPE_CHAUFFAGE')" class="mt-2">
          <b-form-select
            class="form-input custom-placeholder custom-input"
            v-model="form.type_chauffage"
            :options="optionstypechauffage"
            text-field="text"
            value-field="value"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                -- {{ $t("TYPE_CHAUFFAGE") }} --
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <!-- Période de construction -->
        <b-form-group :label="$t('periode_de_construction')">
          <b-form-select
            class="form-input custom-placeholder custom-input"
            v-model="form.periode_de_construction"
            :options="optionsperiode_de_construction"
            text-field="text"
            value-field="value"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                -- {{ $t("periode_de_construction") }} --
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <!-- Parcelle cadastrale -->
        <b-form-group :label="$t('CADASTRAL_PARCEL')" class="mb-4">
          <b-form-input
            type="text"
            :placeholder="$t('CADASTRAL_PARCEL')"
            v-model="form.parcelle_cadastrale"
          />
        </b-form-group>

        <!-- Équipement de pose -->
        <b-form-group :label="$t('equipement')">
          <b-form-select
            :placeholder="$t('equipement')"
            class="form-input custom-placeholder custom-input"
            v-model="form.equipement_deposee"
            :options="optionstypeEquipement_deposee"
            text-field="text"
            value-field="value"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                -- {{ $t("equipement") }} --
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

      
        <b-form-group label="Type d'énergie">
          <b-form-select
            class="form-input custom-placeholder custom-input"
            v-model="form.type_energie_equipement_deposee"
            :options="optionsType_de_combustible"
            text-field="text"
            value-field="value"
          />
        </b-form-group>

        <!-- Altitude / NPDL / NPRM / NPCE (1 champ par ligne) -->
    
       
            <b-form-group :label="$t('Altitude')">
              <b-form-input
                type="number"
                min="0"
                :placeholder="$t('Altitude')"
                v-model="form.altitude"
              />
            </b-form-group>
         
          
            <b-form-group :label="$t('NPDL')" class="mt-2">
              <b-form-input
                type="text"
                :placeholder="$t('NPDL')"
                v-model="form.NPDL"
              />
            </b-form-group>
        
            <b-form-group :label="$t('NPRM')">
              <b-form-input
                type="text"
                :placeholder="$t('NPRM')"
                v-model="form.NPRM"
              />
            </b-form-group>
        
            <b-form-group :label="$t('NPCE')" class="mt-2">
              <b-form-input
                type="text"
                :placeholder="$t('NPCE')"
                v-model="form.NPCE"
              />
            </b-form-group>
        

        <b-form-checkbox
          v-model="form.proprietaire_occupant"
          :true-value="1"
          :false-value="0"
          class="custom-input"
        >
          {{ $t("Propriétaire occupant") }}
        </b-form-checkbox>
     <b-form-checkbox
          v-if="form.proprietaire_occupant"
          v-model="form.residence_principale_proprietaire"
          :true-value="1"
          :false-value="0"
          class="custom-input"
        >
          {{ $t("Résidence principale") }}
        </b-form-checkbox>
      
            <b-form-group label="Secteur d’activité">
              <b-form-input
                type="text"
                v-model="form.secteur_activite"
              />
            </b-form-group>
        
            <b-form-group label="SIRET" class="mt-2">
              <b-form-input
                type="text"
                v-model="form.siret"
              />
            </b-form-group>
        
            <b-form-group label="Nom du site">
              <b-form-input
                type="text"
                v-model="form.nom_site"
              />
            </b-form-group>
         
            <b-form-group label="Complément d'adresse" class="mt-2">
              <b-form-input
                type="text"
                v-model="form.complement_adresse"
              />
            </b-form-group>
      
            <b-form-group label="Quartier QPV">
              <b-form-input
                type="text"
                v-model="form.quartier_qpv"
              />
            </b-form-group>
        
            <b-form-group label="Nom de quartier">
              <b-form-input
                type="text"
                v-model="form.nom_de_quartier"
              />
            </b-form-group>
        
            <b-form-group label="Code de quartier" class="mt-2">
              <b-form-input
                type="text"
                v-model="form.code_de_quartier"
              />
            </b-form-group>
        
      
            <b-form-group label="Surface d’un sous-sol">
              <b-form-input
                type="number"
                min="0"
                v-model="form.surface_au_sol"
              />
            </b-form-group>
       
   
      </div>
    </form>

    <template #modal-footer>
      <b-alert variant="warning" show v-if="errorS" class="messageError">
        {{ errorS }}
      </b-alert>
      <div class="double">
        <b-button variant="danger" @click="resetModal">
          <div class="block-spinner">
            {{ $t("CANCEL") }}
          </div>
        </b-button>
        <b-button variant="success" @click="saveLogement">
          <div class="block-spinner">
            {{ $t("SAVE") }}
            <div v-if="getLoadingLogement" class="loading ml-2">
              <div class="spinner-border" role="status"></div>
            </div>
          </div>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import errorHandle from "../ui/errorHandle.vue";

export default {
  components: { errorHandle },
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    logementToEdit: {
      type: Object,
      default: null,
    },
    /** Contexte client (depuis détail client) : client_id et entreprise pris depuis l'URL / détail, masque les selects Entreprise et Client */
    clientContext: {
      type: Object,
      default: null,
    },
  },
  setup() {
    return { v$: useVuelidate({ $lazy: true, $autoDirty: true }) };
  },
  watch: {
    logementToEdit: {
      handler(newVal) {
        if (newVal && this.isEdit) {
          this.$nextTick(() => {
            this.loadLogementData(newVal);
          });
        }
      },
      immediate: true,
      deep: true,
    },
    selectedEntreprise(newVal) {
      this.form.entreprise = newVal?.id || null;
      // Réinitialiser le client si l'entreprise change
      if (newVal && this.selectedClient) {
        const clientEntrepriseId = this.selectedClient.entreprise?.id || this.selectedClient.entreprise_id;
        if (clientEntrepriseId !== newVal.id) {
          this.selectedClient = null;
          this.form.client_id = null;
        }
      }
    },
    selectedClient(newVal) {
      this.form.client_id = newVal?.id || null;
    },
  },
  data() {
    return {
      selectedEntreprise: null,
      selectedClient: null,
      form: {
        entreprise: null,
        client_id: null,
        adresse: null,
        type: null,
        surface: null,
        code_postal: null,
        ville: null,
        proprietaire_occupant: 0,
        residence_principale_proprietaire: 0,
        type_chauffage: null,
        periode_de_construction: null,
        parcelle_cadastrale: null,
        equipement_deposee: null,
        type_energie_equipement_deposee: null,
        altitude: null,
        NPDL: null,
        NPRM: null,
        NPCE: null,
        secteur_activite: null,
        siret: null,
        nom_site: null,
        complement_adresse: null,
        quartier_qpv: null,
        nom_de_quartier: null,
        code_de_quartier: null,
        surface_au_sol: null,
      },
      errorS: null,
      optionstypelogement: [
        { value: "maison_individuelle", text: this.$t("Maison individuelle") },
        { value: "appartement", text: this.$t("Appartement") },
      ],
      optionstypeEquipement_deposee: [
        { value: "Isolation thermique en combles perdus", text: this.$t("Isolation thermique en combles perdus") },
        { value: "Poêle à granulés", text: this.$t("Poêle à granulés") },
        { value: "Isolation de points singuliers d’un réseau", text: this.$t("Isolation de points singuliers d’un réseau") },
        { value: "Isolation des murs", text: this.$t("Isolation des murs") },
        { value: "Isolation d'un plancher", text: this.$t("Isolation d'un plancher") },
        { value: "Fenêtre ou porte-fenêtre complète avec vitrage isolant", text: this.$t("Fenêtre ou porte-fenêtre complète avec vitrage isolant") },
        { value: "Isolation des toitures terrasses", text: this.$t("Isolation des toitures terrasses") },
        { value: "Chauffe-eau solaire individuel", text: this.$t("Chauffe-eau solaire individuel") },
        { value: "Chaudière biomasse individuelle", text: this.$t("Chaudière biomasse individuelle") },
        { value: "Pompe à chaleur de type air/eau", text: this.$t("Pompe à chaleur de type air/eau") },
        { value: "Rénovation globale d’un bâtiment résidentiel collectif", text: this.$t("Rénovation globale d’un bâtiment résidentiel collectif") },
        { value: "Rénovation d’ampleur d’un appartement", text: this.$t("Rénovation d’ampleur d’un appartement") },
        { value: "Rénovation d’ampleur d’une maison individuelle", text: this.$t("Rénovation d’ampleur d’une maison individuelle") },
      ],
      optionsType_de_combustible: [
        { value: null, text: this.$t("Aucune énergie sélectionnée") },
        { value: "Granulé", text: this.$t("granulé") },
        { value: "Fioul", text: this.$t("Fuel") },
        { value: "Gaz", text: this.$t("Gaz") },
      ],
      optionstypechauffage: [
        { value: "Combustible/bois", text: this.$t("Combustible/Bois") },
        { value: "Electrique", text: this.$t("Electrique") },
        { value: "Gaz", text: this.$t("Gaz") },
        { value: "Fioul", text: this.$t("Fuel") },
        { value: "Bois", text: this.$t("Bois") },
        { value: "ENR/PAC", text: "ENR/PAC" },
      ],
      optionsperiode_de_construction: [
        { value: "moins de 2 ans", text: this.$t("Moins_de_2_ans") },
        {
          value: "de 2 à 10 ans",
          text: this.$t("De 2 à 10 ans"),
        },
        { value: "de 10 à 15 ans", text: this.$t("De 10 à 15 ans") },
        { value: "au moins 15 ans", text: this.$t("Plus_de_15_ans") },
      ],
      page: 1,
      per_page: 100000,
    };
  },
  validations() {
    // Toujours inclure tous les champs dans les validations pour éviter les erreurs $errors undefined
    const baseRules = {
      adresse: { required, maxLength: maxLength(255) },
      code_postal: { required, maxLength: maxLength(10) },
      ville: { required, maxLength: maxLength(100) },
    };
    // Si clientContext (détail client), entreprise et client_id sont fournis par l'URL → pas requis
    const optCompanyClient = this.clientContext ? {} : { entreprise: { required }, client_id: { required } };

    if (!this.isEdit) {
      return {
        form: {
          ...optCompanyClient,
          ...baseRules,
          type: { required },
          surface: { required },
        },
      };
    } else {
      return {
        form: {
          entreprise: {},
          client_id: {},
          ...baseRules,
          type: {},
          surface: {},
        },
      };
    }
  },
  computed: {
    ...mapGetters([
      "getAllentreprises",
      "getAllclients",
      "getLoadingLogement",
    ]),
    computedEntreprises() {
      return this.getAllentreprises || [];
    },
    computedClients() {
      // Optionnel: filtrer par entreprise sélectionnée
      if (!this.getAllclients) return [];
      const entrepriseId = this.selectedEntreprise?.id || this.form.entreprise;
      if (!entrepriseId) {
        return this.getAllclients.map((c) => ({
          ...c,
          fullName: c.nom ? `${c.nom} ${c.prenom || ""}`.trim() : c.company,
        }));
      }
      return this.getAllclients
        .filter(
          (c) =>
            !c.entreprise ||
            c.entreprise.id === entrepriseId ||
            c.entreprise_id === entrepriseId
        )
        .map((c) => ({
          ...c,
          fullName: c.nom ? `${c.nom} ${c.prenom || ""}`.trim() : c.company,
        }));
    },
  },
  methods: {
    ...mapActions(["storeLogement", "editLogement", "all_entreprises", "all_clients"]),
    validateState(name) {
      const field = this.v$.form[name];
      if (!field) return null;
      const { $dirty, $error } = field;
      return $dirty ? !$error : null;
    },
    async saveLogement() {
      const isValid = await this.v$.$validate();
      if (!isValid) return;
      this.errorS = null;

      const payload = {
        client_id: this.form.client_id,
        adresse: this.form.adresse,
        type: this.form.type,
        surface: this.form.surface,
        code_postal: this.form.code_postal,
        ville: this.form.ville,
        proprietaire_occupant: this.form.proprietaire_occupant,
        residence_principale_proprietaire:
          this.form.residence_principale_proprietaire,
        type_chauffage: this.form.type_chauffage,
        periode_de_construction: this.form.periode_de_construction,
        parcelle_cadastrale: this.form.parcelle_cadastrale,
        equipement_deposee: this.form.equipement_deposee,
        type_energie_equipement_deposee:
          this.form.type_energie_equipement_deposee,
        altitude: this.form.altitude,
        NPDL: this.form.NPDL,
        NPRM: this.form.NPRM,
        NPCE: this.form.NPCE,
        secteur_activite: this.form.secteur_activite,
        siret: this.form.siret,
        nom_site: this.form.nom_site,
        complement_adresse: this.form.complement_adresse,
        quartier_qpv: this.form.quartier_qpv,
        nom_de_quartier: this.form.nom_de_quartier,
        code_de_quartier: this.form.code_de_quartier,
        surface_au_sol: this.form.surface_au_sol,
      };

      if (this.clientContext) {
        payload.client_id = this.clientContext.id;
        payload.entreprise = this.clientContext.entreprise?.id ?? this.clientContext.entreprise_id ?? null;
      }

      try {
        let res;
        if (this.isEdit && this.logementToEdit && this.logementToEdit.id) {
          payload.id = this.logementToEdit.id;
          res = await this.editLogement(payload);
        } else {
          res = await this.storeLogement(payload);
        }
        
        if (typeof res === "string") {
          this.errorS = res;
          return;
        }
        this.$emit("updateList", res);
        this.resetModal();
      } catch (e) {
        this.errorS = e || (this.isEdit ? "Erreur lors de la modification du logement" : "Erreur lors de la création du logement");
      }
    },
    onModalShow() {
      if (this.clientContext) {
        // Contexte détail client : client_id depuis l'URL, entreprise depuis le client
        this.form.client_id = this.clientContext.id;
        this.form.entreprise = this.clientContext.entreprise?.id ?? this.clientContext.entreprise_id ?? null;
      }
      if (this.isEdit && this.logementToEdit) {
        this.loadLogementData(this.logementToEdit);
        if (this.clientContext) {
          // Réappliquer le contexte client (priorité URL)
          this.form.client_id = this.clientContext.id;
          this.form.entreprise = this.clientContext.entreprise?.id ?? this.clientContext.entreprise_id ?? null;
        }
      }
    },
    loadLogementData(logement) {
      if (!logement) return;
      
      // Charger les données du logement dans le formulaire
      this.form = {
        entreprise: logement.entreprise?.id || logement.entreprise_id || null,
        client_id: logement.client?.id || logement.client_id || null,
        adresse: logement.adresse || null,
        type: logement.type || null,
        surface: logement.surface || null,
        code_postal: logement.code_postal || null,
        ville: logement.ville || null,
        proprietaire_occupant: logement.proprietaire_occupant || 0,
        residence_principale_proprietaire: logement.residence_principale_proprietaire || 0,
        type_chauffage: logement.type_chauffage || null,
        periode_de_construction: logement.periode_de_construction || null,
        parcelle_cadastrale: logement.parcelle_cadastrale || null,
        equipement_deposee: logement.equipement_deposee || null,
        type_energie_equipement_deposee: logement.type_energie_equipement_deposee || null,
        altitude: logement.altitude || null,
        NPDL: logement.NPDL || null,
        NPRM: logement.NPRM || null,
        NPCE: logement.NPCE || null,
        secteur_activite: logement.secteur_activite || null,
        siret: logement.siret || null,
        nom_site: logement.nom_site || null,
        complement_adresse: logement.complement_adresse || null,
        quartier_qpv: logement.quartier_qpv || null,
        nom_de_quartier: logement.nom_de_quartier || null,
        code_de_quartier: logement.code_de_quartier || null,
        surface_au_sol: logement.surface_au_sol || null,
      };

      // Charger l'entreprise sélectionnée
      this.$nextTick(() => {
        if (this.form.entreprise && this.getAllentreprises) {
          const entreprise = this.getAllentreprises.find(e => e.id === this.form.entreprise);
          if (entreprise) {
            this.selectedEntreprise = entreprise;
          }
        }

        // Charger le client sélectionné
        if (this.form.client_id && this.getAllclients) {
          const client = this.getAllclients.find(c => c.id === this.form.client_id);
          if (client) {
            this.selectedClient = {
              ...client,
              fullName: client.nom ? `${client.nom} ${client.prenom || ""}`.trim() : client.company,
            };
          }
        }
      });
    },
    resetModal() {
      this.errorS = null;
      this.$refs["logementStoreModal"].hide();
      this.selectedEntreprise = null;
      this.selectedClient = null;
      this.form = {
        entreprise: null,
        client_id: null,
        adresse: null,
        type: null,
        surface: null,
        code_postal: null,
        ville: null,
        proprietaire_occupant: 0,
        residence_principale_proprietaire: 0,
        type_chauffage: null,
        periode_de_construction: null,
        parcelle_cadastrale: null,
        equipement_deposee: null,
        type_energie_equipement_deposee: null,
        altitude: null,
        NPDL: null,
        NPRM: null,
        NPCE: null,
        secteur_activite: null,
        siret: null,
        nom_site: null,
        complement_adresse: null,
        quartier_qpv: null,
        nom_de_quartier: null,
        code_de_quartier: null,
        surface_au_sol: null,
      };
      this.$nextTick(() => {
        this.v$.$reset();
      });
      // Si c'est un modal d'édition, réinitialiser les props
      if (this.isEdit) {
        this.$emit('close');
      }
    },
  },
  mounted() {
    // Précharger entreprises et clients si pas déjà fait
    if (!this.getAllentreprises || !this.getAllentreprises.length) {
      this.all_entreprises({ page: 1, per_page: 1000, with_default: 1 });
    }
    if (!this.getAllclients || !this.getAllclients.length) {
      this.all_clients({
        search: "",
        page: this.page,
        per_page: this.per_page,
      });
    }
  },
};
</script>

<style scoped>
.messageError {
  margin-bottom: 0.5rem;
}
</style>

